import classNames from 'classnames';
import { motion, Reorder } from 'framer-motion';
import * as _ from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import Button from '../../components/Button';
import type QuillEditor from '../QuillEditor';
import type { CarouselImages, CarouselItem } from './Carousel';
import { areCarouselImages, useCarousel } from './Carousel';
import type { FileDragItem, ThumbnailDragItem } from './CarouselThumbnail';
import CarouselThumbnail, {
  CAROUSEL_ITEM_DRAG_TYPE,
} from './CarouselThumbnail';
import * as styles from './CarouselThumbnailBar.module.scss';
import CarouselThumbnailDragPreview from './CarouselThumbnailDragPreview';
import { isEditorCompatibleImage } from '../editorImageMimeTypes';

type ThumbnailItem = {
  id: string;
  image: CarouselItem;
};

function itemFromImage(image: CarouselItem): ThumbnailItem {
  return {
    id: _.uniqueId('thumbnail-'),
    image,
  };
}

const Placeholder = () => (
  <motion.div
    layoutId="placeholder"
    layout="position"
    className={styles.placeholder}
    exit={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    initial={{ opacity: 0 }}
  />
);

interface CarouselThumbnailBarProps {
  onReorder?: (images: CarouselImages) => void;
  onRemove?: (image: CarouselItem) => void;
  onAddImages?: (files: File[], index: number) => void;
  quill?: QuillEditor | null;
  readOnly?: boolean;
}

const CarouselThumbnailBar = ({
  onReorder,
  onRemove,
  onAddImages,
  quill,
  readOnly = true,
}: CarouselThumbnailBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastItemRef = useRef<HTMLButtonElement>(null);
  const [hoverFileIndex, setHoverFileIndex] = useState<number | null>(null);
  const { images, currentIndex, dispatch } = useCarousel();

  const [thumbnailItems, setThumbnailItems] = useState<ThumbnailItem[]>(() =>
    images.map(itemFromImage),
  );

  const resetThumbnailItems = useCallback(
    () => setThumbnailItems(images.map(itemFromImage)),
    [images],
  );

  useEffect(() => resetThumbnailItems(), [resetThumbnailItems]);

  const moveImage = useCallback((dragIndex: number, hoverIndex: number) => {
    setThumbnailItems((current) => {
      const dragImage = current[dragIndex];
      if (!dragImage) return current;
      const next: ThumbnailItem[] = [...current];
      next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, dragImage);
      return next;
    });
  }, []);

  const [{ isOver }, connectContainer] = useDrop<
    ThumbnailDragItem | FileDragItem,
    void,
    { isOver: boolean }
  >({
    canDrop: () => !readOnly,
    accept: [CAROUSEL_ITEM_DRAG_TYPE, NativeTypes.FILE],
    hover(item, monitor) {
      if (!readOnly && monitor.isOver({ shallow: true }) && 'files' in item) {
        const rect = lastItemRef.current?.getBoundingClientRect();
        const clientOffset = monitor.getClientOffset();
        if (!rect || !clientOffset) return;

        if (clientOffset.x > rect.right) {
          setHoverFileIndex(thumbnailItems.length);
        }
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop(item) {
      if ('files' in item && onAddImages && hoverFileIndex != null) {
        onAddImages(item.files.filter(isEditorCompatibleImage), hoverFileIndex);
        setHoverFileIndex(null);
      }
    },
  });
  connectContainer(containerRef);

  useEffect(() => {
    if (!isOver) {
      setHoverFileIndex(null);
    }
  }, [isOver]);

  return (
    <Reorder.Group
      // We use the reorder components only for the layout animation
      // the reordering itself will be handled via react-dnd, because we want
      // to support dragging to the outside of this group to move / remove images
      onReorder={_.noop}
      values={thumbnailItems}
      className={styles.container}
      ref={containerRef}
      as="div"
      axis="x"
    >
      {quill && <CarouselThumbnailDragPreview quill={quill} />}

      {thumbnailItems.map((item, i) => {
        const isSelected = i === currentIndex;

        return (
          <React.Fragment key={item.id}>
            {i === hoverFileIndex && <Placeholder key="placeholder" />}
            <Reorder.Item as="div" value={item} dragListener={!readOnly}>
              <Button
                className={classNames(styles.thumbnailButton, {
                  [styles.isSelected]: isSelected,
                })}
                ref={lastItemRef}
                onClick={() =>
                  dispatch({
                    type: 'select',
                    image: item.image,
                  })
                }
              >
                <div className={styles.overlay} />
                <CarouselThumbnail
                  image={item.image}
                  index={i}
                  moveImage={moveImage}
                  hoverFile={setHoverFileIndex}
                  onDragEnd={(didDrop, removed) => {
                    if (!didDrop) {
                      resetThumbnailItems();
                    } else if (!removed) {
                      const carouselItems = thumbnailItems.map(
                        ({ image }) => image,
                      );
                      if (areCarouselImages(carouselItems)) {
                        onReorder?.(carouselItems);
                      }
                    }
                    setHoverFileIndex(null);
                  }}
                  onRemove={() => onRemove?.(item.image)}
                  canDrag={!readOnly}
                />
              </Button>
            </Reorder.Item>
          </React.Fragment>
        );
      })}

      {hoverFileIndex === thumbnailItems.length && (
        <Placeholder key="placeholder" />
      )}
    </Reorder.Group>
  );
};

export default CarouselThumbnailBar;
